/**
 * Shared presentational primitives for the customer detail page.
 *
 * These keep every section visually consistent and theme-driven: cards use the
 * `card`/`border`/`muted` tokens (no hardcoded palette, no gradients), so they
 * adapt to light/dark automatically. Status tones are the single source of truth
 * for coloured badges and include dark-mode variants.
 */
import type { ReactNode, ElementType } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'violet' | 'neutral';

/** Tinted, border-only tones that read correctly on both light and dark surfaces. */
export const TONE_CLASSES: Record<Tone, string> = {
  success: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  warning: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  danger: 'border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-400',
  info: 'border-blue-500/25 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  violet: 'border-violet-500/25 bg-violet-500/10 text-violet-700 dark:text-violet-400',
  neutral: 'border-border bg-muted text-muted-foreground',
};

export function ToneBadge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Badge variant="outline" className={cn('font-medium capitalize', TONE_CLASSES[tone], className)}>
      {children}
    </Badge>
  );
}

/** A titled Card with an icon-in-chip header. The building block for every section. */
export function SectionCard({
  icon: Icon,
  title,
  description,
  action,
  children,
  className,
  contentClassName,
}: {
  icon: ElementType;
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b bg-muted/30 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <CardTitle className="truncate text-sm font-semibold">{title}</CardTitle>
            {description && <p className="mt-0.5 truncate text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {action}
      </CardHeader>
      <CardContent className={cn('p-5', contentClassName)}>{children}</CardContent>
    </Card>
  );
}

/** Label-over-value tile, for a grid of scalar facts. */
export function FieldTile({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border bg-muted/40 p-3', className)}>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-semibold text-foreground">{children}</div>
    </div>
  );
}

/** Inline label/value row, for lists of key/value pairs. */
export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

/** Labelled row of tag badges; renders nothing when the list is empty. */
export function TagList({
  label,
  items,
  tone,
  className,
}: {
  label: string;
  items: Array<string | null | undefined>;
  tone?: Tone;
  className?: string;
}) {
  const clean = items.filter((v): v is string => Boolean(v));
  if (clean.length === 0) return null;
  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {clean.map((item, i) => (
          <Badge key={`${item}-${i}`} variant="outline" className={cn('font-normal', tone && TONE_CLASSES[tone])}>
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** Compact label/value description list — the dense, scannable default for admin facts. */
export function DescriptionList({
  items,
  className,
}: {
  items: Array<{ label: string; value: ReactNode }>;
  className?: string;
}) {
  const clean = items.filter((i) => i.value !== null && i.value !== undefined && i.value !== '');
  if (clean.length === 0) return null;
  return (
    <dl className={cn('grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2', className)}>
      {clean.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-0.5 border-b border-border/40 pb-2.5 last:border-0 sm:[&:nth-last-child(2)]:border-0">
          <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
          <dd className="text-sm font-medium text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ icon: Icon, message }: { icon: ElementType; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 py-5 text-center text-muted-foreground">
      <Icon className="h-6 w-6 opacity-40" aria-hidden />
      <p className="text-sm">{message}</p>
    </div>
  );
}

/** A bordered timeline entry (work history, education, projects, certifications). */
export function TimelineItem({
  title,
  subtitle,
  meta,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      {meta && <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">{meta}</div>}
      {children && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  );
}

/** External link chip — neutral, theme-driven (no per-brand background tints). */
export function LinkChip({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: ElementType;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group inline-flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm font-medium transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden />
      {label}
    </a>
  );
}
